import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { SERVICE_OPTIONS, COUNTRY_CODES, COMPANY } from "../data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const schema = z.object({
  fullName: z.string().trim().min(2, "Please enter your full name."),
  email: z.string().trim().email("Please enter a valid business email address."),
  countryCode: z.string().min(1, "Please select a country code."),
  phone: z
    .string()
    .trim()
    .regex(/^[0-9][0-9\s-]{5,14}$/, "Please enter a valid phone number (6–15 digits)."),
  company: z.string().trim().min(2, "Please enter your company name."),
  service: z.string().min(1, "Please select the service you require."),
  details: z
    .string()
    .trim()
    .min(20, "Please describe your project in at least 20 characters.")
    .max(2000, "Please keep your project details under 2000 characters."),
  contactMethod: z.enum(["email", "phone"], {
    required_error: "Please choose a preferred contact method.",
  }),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please provide your consent so we can respond to your enquiry." }),
  }),
});

const EnquiryForm = ({ variant = "contact" }) => {
  const [submitted, setSubmitted] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      email: "",
      countryCode: "+91",
      phone: "",
      company: "",
      service: "",
      details: "",
      contactMethod: undefined,
      consent: false,
    },
  });

  const onSubmit = async (data) => {
    setSubmitError(null);
    try {
      await axios.post(`${API}/api/enquiries`, { ...data, variant });
      setSubmitted(data);
      reset();
    } catch (err) {
      setSubmitError(
        `We could not submit your ${
          variant === "consultation" ? "consultation request" : "enquiry"
        } right now. Please try again in a moment, or email us directly at ${COMPANY.email}.`
      );
    }
  };

  const heading = variant === "consultation" ? "Schedule a Consultation" : "Send Us an Enquiry";

  if (submitted) {
    return (
      <div
        className="border border-border bg-white p-8 shadow-sm lg:p-12"
        data-testid="enquiry-form-success"
      >
        <CheckCircle2 className="h-10 w-10 text-brand-red" aria-hidden="true" />
        <h3 className="mt-5 text-2xl font-semibold">Thank you, {submitted.fullName}.</h3>
        <p className="mt-3 text-base leading-relaxed text-brand-grey">
          Your {variant === "consultation" ? "consultation request" : "enquiry"} has been received
          and forwarded to our team. We will get back to you shortly using your preferred contact
          method. For anything urgent, you can also reach us at{" "}
          <a
            href={`mailto:${COMPANY.email}`}
            className="font-medium text-brand-red underline underline-offset-2"
            data-testid="success-email-link"
          >
            {COMPANY.email}
          </a>
          .
        </p>
        <button
          type="button"
          className="btn-secondary mt-8"
          onClick={() => setSubmitted(null)}
          data-testid="enquiry-form-reset-button"
        >
          Submit Another Enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="border border-border bg-white p-8 shadow-sm lg:p-12"
      data-testid="enquiry-form"
    >
      <h3 className="text-2xl font-semibold" data-testid="enquiry-form-heading">
        {heading}
      </h3>
      <p className="mt-2 text-sm text-brand-grey">
        Fields marked with <span className="text-brand-red">*</span> are required.
      </p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="fullName" className="field-label">
            Full Name <span className="text-brand-red">*</span>
          </label>
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            className="field-input"
            placeholder="Your full name"
            data-testid="enquiry-fullname-input"
            aria-invalid={!!errors.fullName}
            {...register("fullName")}
          />
          {errors.fullName && (
            <p className="field-error" role="alert" data-testid="enquiry-fullname-error">
              {errors.fullName.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="field-label">
            Business Email <span className="text-brand-red">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="field-input"
            placeholder="you@company.com"
            data-testid="enquiry-email-input"
            aria-invalid={!!errors.email}
            {...register("email")}
          />
          {errors.email && (
            <p className="field-error" role="alert" data-testid="enquiry-email-error">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="field-label">
            Phone Number <span className="text-brand-red">*</span>
          </label>
          <div className="flex gap-3">
            <select
              aria-label="Country code"
              className="field-input w-36 shrink-0"
              data-testid="enquiry-country-code-select"
              {...register("countryCode")}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <input
              id="phone"
              type="tel"
              autoComplete="tel-national"
              className="field-input"
              placeholder="Phone number"
              data-testid="enquiry-phone-input"
              aria-invalid={!!errors.phone}
              {...register("phone")}
            />
          </div>
          {errors.phone && (
            <p className="field-error" role="alert" data-testid="enquiry-phone-error">
              {errors.phone.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="company" className="field-label">
            Company Name <span className="text-brand-red">*</span>
          </label>
          <input
            id="company"
            type="text"
            autoComplete="organization"
            className="field-input"
            placeholder="Your company"
            data-testid="enquiry-company-input"
            aria-invalid={!!errors.company}
            {...register("company")}
          />
          {errors.company && (
            <p className="field-error" role="alert" data-testid="enquiry-company-error">
              {errors.company.message}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="service" className="field-label">
            Service Required <span className="text-brand-red">*</span>
          </label>
          <select
            id="service"
            className="field-input"
            data-testid="enquiry-service-select"
            aria-invalid={!!errors.service}
            {...register("service")}
          >
            <option value="">Select a service…</option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            <option value="Other">Other / Not sure yet</option>
          </select>
          {errors.service && (
            <p className="field-error" role="alert" data-testid="enquiry-service-error">
              {errors.service.message}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="details" className="field-label">
            Project Details <span className="text-brand-red">*</span>
          </label>
          <textarea
            id="details"
            rows={5}
            className="field-input resize-y"
            placeholder="Tell us about your business challenge, product idea or technology requirement."
            data-testid="enquiry-details-textarea"
            aria-invalid={!!errors.details}
            {...register("details")}
          />
          {errors.details && (
            <p className="field-error" role="alert" data-testid="enquiry-details-error">
              {errors.details.message}
            </p>
          )}
        </div>

        <fieldset className="sm:col-span-2">
          <legend className="field-label">
            Preferred Contact Method <span className="text-brand-red">*</span>
          </legend>
          <div className="flex flex-wrap gap-6" data-testid="enquiry-contact-method-group">
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-brand-graphite">
              <input
                type="radio"
                value="email"
                className="h-4 w-4 accent-brand-red"
                data-testid="enquiry-contact-method-email"
                {...register("contactMethod")}
              />
              Email
            </label>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-brand-graphite">
              <input
                type="radio"
                value="phone"
                className="h-4 w-4 accent-brand-red"
                data-testid="enquiry-contact-method-phone"
                {...register("contactMethod")}
              />
              Phone
            </label>
          </div>
          {errors.contactMethod && (
            <p className="field-error" role="alert" data-testid="enquiry-contact-method-error">
              {errors.contactMethod.message}
            </p>
          )}
        </fieldset>

        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-brand-grey">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-brand-red"
              data-testid="enquiry-consent-checkbox"
              aria-invalid={!!errors.consent}
              {...register("consent")}
            />
            <span>
              I agree that Vaskion Technologies Private Limited may use the information provided to respond to my
              enquiry. <span className="text-brand-red">*</span>
            </span>
          </label>
          {errors.consent && (
            <p className="field-error" role="alert" data-testid="enquiry-consent-error">
              {errors.consent.message}
            </p>
          )}
        </div>
      </div>

      {submitError && (
        <p
          className="mt-6 flex items-start gap-2 border-l-2 border-brand-red bg-brand-surface p-4 text-sm leading-relaxed text-brand-graphite"
          role="alert"
          data-testid="enquiry-submit-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" />
          <span>{submitError}</span>
        </p>
      )}

      <button
        type="submit"
        className="btn-primary mt-8 w-full sm:w-auto"
        disabled={isSubmitting}
        data-testid="enquiry-submit-button"
      >
        {isSubmitting
          ? "Submitting…"
          : variant === "consultation"
          ? "Request Consultation"
          : "Submit Enquiry"}
      </button>

      <p className="mt-4 text-xs text-neutral-500" data-testid="enquiry-privacy-note">
        Your details are stored securely and forwarded to our team at {COMPANY.email}.
      </p>
    </form>
  );
};

export default EnquiryForm;
